import { VerificationsAndAutoMatchServerResponse } from '@mortee/domain/validationSystem';
import React from 'react';
import { StatusType } from './NewVerificationTab.styles';

export type TableColumn = { key: string; title: string };
export type GenericTableRow = Record<string, React.ReactNode>;
export type EvidenceItem = {
  value: string;
  source?: string;
  evidenceType?: string;
  // For legalIdentifiers presets: the original, unparsed parts, so selecting a preset fills the edit
  // dialog's value/type/country from these directly instead of splitting the formatted `value` string
  // (which breaks when the identifier value itself contains " - ", e.g. "30-71234567-9 - VAT (AR)").
  legalId?: { value: string; typeName: string | null; countryCode: string | null };
};

export interface AccountSection {
  id: string;
  rows: ExpandableTableRow[];
  validationType: string | undefined;
  privateAccount: boolean;
  /**
   * The OTHER flow's raw saved values, captured at build time. The hint string itself is
   * recomputed per render against the section's CURRENT values (computeAccountFlowDiffs), so it
   * clears the moment the user reconciles the field. Omitted on new/duplicated sections.
   */
  otherFlowValues?: {
    validationType?: string;
    privateAccount?: boolean;
  };
  /**
   * staticId of the stored Manual-flow verification record this section maps to, if any.
   * Captured at build time while server arrays are still positionally aligned, so save/delete
   * target the right record even after sections are added, duplicated, or removed.
   */
  manualVerificationStaticId?: string;
  /** staticId of the stored Final-flow verification record this section maps to, if any. */
  finalVerificationStaticId?: string;
  /** staticId of the second underlying account (the additional-bank-code duplicate), Manual flow. */
  secondaryManualVerificationStaticId?: string;
  /** staticId of the second underlying account (the additional-bank-code duplicate), Final flow. */
  secondaryFinalVerificationStaticId?: string;
}

export type DataPointType =
  | 'country'
  | 'name'
  | 'address'
  | 'legalIdentifiers'
  | 'accountCountry'
  | 'bankName'
  | 'bankCode'
  | 'branchCode'
  | 'iban'
  | 'swiftCode'
  | 'accountNumber'
  | 'forFurtherCredit'
  | 'clabe';

export type ExpandableTableRow = {
  dataPoint: React.ReactNode;
  dataPointType: DataPointType;
  vendor: React.ReactNode | EvidenceItem[];
  evidence: EvidenceItem[] | undefined;
  automatedMatching: StatusType | undefined;
  manualProcess: React.ReactNode;
  finalReview: React.ReactNode;
  /** True when the server reports this field changed from its initial state (Manual flow) */
  manualChangedFromInitial?: boolean;
  /** True when the server reports this field changed from its initial state (Final flow) */
  finalChangedFromInitial?: boolean;
  /**
   * Whether the user set this flow's value during the current session (see applyReviewCellEdit).
   * GenericTable also tracks its own edit-dialog commits in a per-cell set keyed by row id, but that
   * set is invisible to anything outside the table — so these flags are what let table-level logic
   * (the Approve All count, the completion blocker) agree with the colors the cells show. Dropped
   * when the rows are rebuilt from server data, where the changedFromInitial flags take over.
   */
  manualEditedInSession?: boolean;
  finalEditedInSession?: boolean;
  /**
   * When the Manual flow's value was last changed (epoch millis). Null/undefined when it never
   * changed from the record's initial version — an un-edited Manual value never trumps.
   * Stamped with Date.now() on an in-session Manual edit (see applyReviewCellEdit).
   */
  manualLastChangeTimestamp?: number | null;
  /**
   * Effective timestamp of the Final flow's value: the field's lastChangeTimestamp, falling back to
   * the Final record's writeTimestamp when the field never changed (its value dates from the record's
   * creation — e.g. an autoMatch seed — whose exact time isn't exposed; the record's last write is a
   * conservative upper bound, so a stale Manual value never trumps a newer Final one).
   * Stamped with Date.now() on an in-session Final edit (see applyReviewCellEdit).
   */
  finalLastChangeTimestamp?: number | null;
  expandable?: boolean;
  subRows?: GenericTableRow[];
  showAddIcon?: boolean;
  showDeleteIcon?: boolean;
  isChildRow?: boolean;
  parentIndex?: number;
  reason?: string;
};

export const PAYEE_DETAILS_COLUMNS: TableColumn[] = [
  { key: 'dataPoint', title: 'Data Point' },
  { key: 'vendor', title: 'Vendor Registration' },
  { key: 'evidence', title: 'Evidence/Enrichment' },
  { key: 'automatedMatching', title: 'Auto Matching' },
  { key: 'manualProcess', title: 'Manual Processing' },
  { key: 'finalReview', title: 'Final Review' },
];

export const INITIAL_PAYEE_DETAILS_ROWS: ExpandableTableRow[] = [
  {
    dataPoint: 'Country',
    dataPointType: 'country',
    vendor: undefined,
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'Name',
    dataPointType: 'name',
    vendor: undefined,
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
    showAddIcon: true,
  },
  {
    dataPoint: 'Address',
    dataPointType: 'address',
    vendor: undefined,
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
    showAddIcon: true,
  },
  {
    dataPoint: 'Legal Identifier',
    dataPointType: 'legalIdentifiers',
    vendor: undefined,
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
    showAddIcon: true,
  },
];

export const ACCOUNT_DETAILS_COLUMNS: TableColumn[] = [
  { key: 'dataPoint', title: 'Data Point' },
  { key: 'vendor', title: 'Vendor Registration' },
  { key: 'evidence', title: 'Evidence/Enrichment' },
  { key: 'automatedMatching', title: 'Auto Matching' },
  { key: 'manualProcess', title: 'Manual Processing' },
  { key: 'finalReview', title: 'Final Review' },
];

export const INITIAL_ACCOUNT_DETAILS_ROWS: ExpandableTableRow[] = [
  {
    dataPoint: 'Account Country',
    vendor: undefined,
    dataPointType: 'accountCountry',
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'Bank Name',
    vendor: undefined,
    dataPointType: 'bankName',
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'Bank Code',
    vendor: undefined,
    dataPointType: 'bankCode',
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
    showAddIcon: true,
  },
  {
    dataPoint: 'Branch Code',
    vendor: undefined,
    dataPointType: 'branchCode',
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'IBAN',
    vendor: undefined,
    dataPointType: 'iban',
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'Swift Code',
    vendor: undefined,
    dataPointType: 'swiftCode',
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'Account Number',
    vendor: undefined,
    dataPointType: 'accountNumber',
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'For Further Credit',
    vendor: undefined,
    dataPointType: 'forFurtherCredit',
    evidence: undefined,
    automatedMatching: undefined,
    manualProcess: undefined,
    finalReview: undefined,
  },
];

export const ACCOUNT_DETAILS_ROWS1: ExpandableTableRow[] = [
  {
    dataPoint: 'Account Country',
    vendor: 'United States',
    dataPointType: 'accountCountry',
    evidence: [{ value: 'United States', source: '(BOFA)' }],
    automatedMatching: 'Match',
    manualProcess: 'United States',
    finalReview: 'United States',
  },
  {
    dataPoint: 'Bank Name',
    dataPointType: 'bankName',
    vendor: 'Name Bank',
    evidence: [{ value: 'Name Bank', source: '(BOFA)' }],
    automatedMatching: 'Match',
    manualProcess: 'Name Bank',
    finalReview: 'Name Bank',
  },
  {
    dataPoint: 'Bank Code',
    dataPointType: 'bankCode',
    vendor: '12345678',
    evidence: undefined,
    automatedMatching: 'Mismatch',
    manualProcess: undefined,
    finalReview: undefined,
    showAddIcon: true,
  },
  {
    dataPoint: 'Branch Code',
    dataPointType: 'branchCode',
    vendor: undefined,
    evidence: undefined,
    automatedMatching: 'NotRelevant',
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'IBAN',
    dataPointType: 'iban',
    vendor: undefined,
    evidence: undefined,
    automatedMatching: 'NotRelevant',
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'Swift Code',
    dataPointType: 'swiftCode',
    vendor: undefined,
    evidence: undefined,
    automatedMatching: 'NotRelevant',
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'Account Number',
    dataPointType: 'accountNumber',
    vendor: undefined,
    evidence: undefined,
    automatedMatching: 'NotRelevant',
    manualProcess: undefined,
    finalReview: undefined,
  },
  {
    dataPoint: 'For further credit',
    dataPointType: 'forFurtherCredit',
    vendor: undefined,
    evidence: undefined,
    automatedMatching: 'NotRelevant',
    manualProcess: undefined,
    finalReview: undefined,
  },
];

export const createMockVerificationsAndAutoMatchServerResponse = (
  validationRecordStaticId: string,
): VerificationsAndAutoMatchServerResponse => {
  const now = Date.now();

  return {
    manualVerification: {
      payee: {
        validationRecordStaticId: {
          value: validationRecordStaticId,
          hasChangedFromInitialState: false,
          lastChangeTimestamp: null,
        },
        name: { value: 'Test Vendor', hasChangedFromInitialState: false, lastChangeTimestamp: null },
        additionalName: { value: '', hasChangedFromInitialState: false, lastChangeTimestamp: null },
        address: {
          value: 'The White House, Washington, United States',
          hasChangedFromInitialState: false,
          lastChangeTimestamp: null,
        },
        formattedAddress: {
          value: 'The White House, Washington, United States',
          hasChangedFromInitialState: false,
          lastChangeTimestamp: null,
        },
        additionalAddress: { value: '', hasChangedFromInitialState: false, lastChangeTimestamp: null },
        countryCode: { value: 'US', hasChangedFromInitialState: false, lastChangeTimestamp: null },
        legalEntityIdentifiers: [
          {
            value: {
              typeId: 'taxid-type-1',
              typeName: 'TaxId',
              countryCode: 'US',
              value: '12345678',
              isPrimary: true,
            },
            hasChangedFromInitialState: false,
            lastChangeTimestamp: null,
          },
          {
            value: {
              typeId: 'duns-type-1',
              typeName: 'Duns',
              countryCode: null,
              value: '123456789',
              isPrimary: false,
            },
            hasChangedFromInitialState: false,
            lastChangeTimestamp: null,
          },
        ],
        strongLegalIdentifies: {
          DUNS: [
            {
              value: {
                typeId: 'duns-type-1',
                typeName: 'Duns',
                countryCode: null,
                value: '123456789',
                isPrimary: false,
              },
              hasChangedFromInitialState: false,
              lastChangeTimestamp: null,
            },
          ],
        },
        sanctionsScreening: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        alreadyExistingValidatedPayee: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        userId: { value: 'user-123', hasChangedFromInitialState: false, lastChangeTimestamp: null },
        writeTimestamp: { value: now, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        createdValidatedPayeeId: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        isPrivate: { value: false, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        effectiveTimestamp: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        isUpdateRequired: { value: false, hasChangedFromInitialState: false, lastChangeTimestamp: null },
      },
      accounts: [
        {
          staticId: { value: 'account-123', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          validationRecordStaticId: {
            value: validationRecordStaticId,
            hasChangedFromInitialState: false,
            lastChangeTimestamp: null,
          },
          countryCode: { value: 'US', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          swift: { value: '12345678', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          bankCode: { value: '12345678', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          bankName: { value: 'Name Bank', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          branchCode: { value: '12345678', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          accountNumber: { value: '12345678', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          iban: { value: '12345678', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          furtherCredit: { value: '12345678', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          validationTimestamp: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          type: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          achDidReceiveAccount: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          achDidReceiveEin: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          userId: { value: 'user-123', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          writeTimestamp: { value: now, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          creationTimestamp: { value: now, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          createdValidatedAccountId: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          localFormat: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          isPrivate: { value: false, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        },
      ],
    },
    finalVerification: {
      payee: {
        validationRecordStaticId: {
          value: validationRecordStaticId,
          hasChangedFromInitialState: false,
          lastChangeTimestamp: null,
        },
        name: { value: 'Test Vendor', hasChangedFromInitialState: false, lastChangeTimestamp: null },
        additionalName: { value: null, hasChangedFromInitialState: true, lastChangeTimestamp: null },
        address: {
          value: 'The White House, Washington, United States',
          hasChangedFromInitialState: false,
          lastChangeTimestamp: null,
        },
        formattedAddress: {
          value: 'The White House, Washington, United States',
          hasChangedFromInitialState: false,
          lastChangeTimestamp: null,
        },
        additionalAddress: { value: null, hasChangedFromInitialState: true, lastChangeTimestamp: null },
        countryCode: { value: '', hasChangedFromInitialState: false, lastChangeTimestamp: null },
        legalEntityIdentifiers: [
          {
            value: {
              typeId: 'taxid-type-1',
              typeName: 'TaxId',
              countryCode: 'US',
              value: '12345678',
              isPrimary: true,
            },
            hasChangedFromInitialState: false,
            lastChangeTimestamp: null,
          },
          {
            value: {
              typeId: 'duns-type-1',
              typeName: 'Duns',
              countryCode: null,
              value: '123456',
              isPrimary: false,
            },
            hasChangedFromInitialState: true,
            lastChangeTimestamp: null,
          },
        ],
        strongLegalIdentifies: {
          DUNS: [
            {
              value: {
                typeId: 'duns-type-1',
                typeName: 'Duns',
                countryCode: null,
                value: '123456',
                isPrimary: false,
              },
              hasChangedFromInitialState: true,
              lastChangeTimestamp: null,
            },
          ],
        },
        sanctionsScreening: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        alreadyExistingValidatedPayee: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        userId: { value: 'user-123', hasChangedFromInitialState: false, lastChangeTimestamp: null },
        writeTimestamp: { value: now + 1000, hasChangedFromInitialState: true, lastChangeTimestamp: null },
        createdValidatedPayeeId: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        isPrivate: { value: false, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        effectiveTimestamp: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        isUpdateRequired: { value: false, hasChangedFromInitialState: false, lastChangeTimestamp: null },
      },
      accounts: [
        {
          staticId: { value: 'account-456', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          validationRecordStaticId: {
            value: validationRecordStaticId,
            hasChangedFromInitialState: false,
            lastChangeTimestamp: null,
          },
          countryCode: { value: 'US', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          swift: { value: '12345678', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          bankCode: { value: '12345678', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          bankName: { value: 'Name Bank', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          branchCode: { value: '12345678', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          accountNumber: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          iban: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          furtherCredit: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          validationTimestamp: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          type: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          achDidReceiveAccount: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          achDidReceiveEin: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          userId: { value: 'user-123', hasChangedFromInitialState: false, lastChangeTimestamp: null },
          writeTimestamp: { value: now, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          creationTimestamp: { value: now, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          createdValidatedAccountId: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          localFormat: { value: null, hasChangedFromInitialState: false, lastChangeTimestamp: null },
          isPrivate: { value: false, hasChangedFromInitialState: false, lastChangeTimestamp: null },
        },
      ],
    },
    autoMatchResult: {
      validationRecordStaticId: validationRecordStaticId,
      autoMatchDataSet: {
        // Payee Data Points (can be null if not applicable)
        payeeCountry: null,
        payeeName: { result: 'PartialMatch', partialMatchReasons: ['MinorTypo'] },
        address: { result: 'Mismatch', partialMatchReasons: [] },
        legalIdentifiersByValue: null,
        // Account Data Points (can be null if not applicable)
        accountCountry: null,
        bankName: null,
        bankCode: null,
        abaRouting: null,
        branchCode: null,
        swift: null,
        iban: null,
        accountNumber: null,
        furtherCredit: null,
      },
    },
    databaseEvidence: {
      validationRecordStaticId: validationRecordStaticId,
      validationRecordPresentationId: 'VR-7',
      timestamp: now,
      evidenceDataPoints: [
        {
          evidenceId: '5169839c-4238-4e10-a278-5b1804881573',
          evidenceType: 'VatSearch',
          swiftData: {
            swiftCode: null,
            bankName: null,
          },
          countryBankCodeData: {
            countryCode: null,
            bankCode: null,
            bankName: null,
          },
        },
        {
          evidenceId: '352fc28e-1757-47f3-8a72-e1df2fc0465c',
          evidenceType: 'IbanCalc',
          swiftData: {
            swiftCode: 'HBUKGB4BXXX',
            bankName: null,
          },
          countryBankCodeData: {
            countryCode: 'GB',
            bankCode: '401276',
            bankName: null,
          },
        },
      ],
      registrationAbaRoutingData: {
        countryCode: 'US',
        bankCode: '1234567890',
        bankName: null,
      },
      registrationSwiftData: {
        swiftCode: 'HBUKGB4B',
        bankName: null,
      },
      registrationCountryBankCodeData: {
        countryCode: 'GB',
        bankCode: '401276',
        bankName: null,
      },
    },
  };
};
